import UserAvatar from "@/components/lazo-ai-chat-components/Avatar";
import LazoTooltip from "@/components/utils/LazoTooltip";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import User from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/User";
import StringUtils from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/StringUtils";
import { useResponsive } from "@/hooks/useResponsive";
import { LazoChatConversation } from "@/models/ai-chat/LazoChatConversation";
import { LazoTeamOfCompany } from "@/types/LazoTeamOfCompany";
import { EnumsUtils } from "@/utils/TaskEnumUtils";
import { Close, Groups, People } from "@mui/icons-material";
import {
  Box,
  Button,
  Fab,
  Fade,
  IconButton,
  MenuItem,
  MenuList,
  SwipeableDrawer,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserAvatarSx } from "../utilStyles";
import { OneGroupChatModal } from "./OneGroupChatModal";

type Props = {
  onEmployeeClick: (e: LazoTeamOfCompany) => void;
  onCreateGroupChat: (members: string[]) => Promise<ApiResponseV2<LazoChatConversation>>;
  onClickSupportChat: () => void;
};

export const LazoExpertsList = React.memo(function LazoExpertsList(props: Props) {
  const { t } = useTranslation("chat");
  const theme = useTheme();

  const [openGroupModal, setOpenGroupModal] = useState<boolean>(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState<boolean>(false);

  const { isMobile, isTablet, isDesktop } = useResponsive();
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const { lazoEmployees: fullLazoEmployees } = useTenantMemberships();

  const lazoEmployees = useMemo(() => {
    const mergedEmployees = fullLazoEmployees.reduce((row, employee) => {
      const existing = row.get(employee.userExternalId);

      if (!existing) {
        row.set(employee.userExternalId, { ...employee });
        return row;
      }

      existing.role = Array.from(new Set([...existing.role.split(", "), employee.role])).join(", ");
      if (!existing.service) {
        existing.service = employee.service;
      } else {
        existing.service = Array.from(new Set([...existing.service.split(", "), employee.service])).join(", ");
      }

      return row;
    }, new Map<string, LazoTeamOfCompany>());

    const employees = Array.from(mergedEmployees.values());

    const kamIdx = employees.findIndex((e) => e.role.includes("KAM"));
    if (kamIdx !== 0 && kamIdx !== -1) {
      [employees[0], employees[kamIdx]] = [employees[kamIdx], employees[0]];
    }

    return employees;
  }, [fullLazoEmployees]);

  const getUser = useCallback((e: LazoTeamOfCompany): User => {
    const [fname, lname] = e.fullName.split(" ");
    return {
      externalId: e.userExternalId,
      firstName: fname,
      lastName: lname,
      email: e.email,
    } as User;
  }, []);

  const handleEmployeeClick = useCallback(
    (employee: LazoTeamOfCompany) => {
      props.onEmployeeClick(employee);
      if (mobileDrawerOpen) {
        setMobileDrawerOpen(false);
      }
    },
    [props, mobileDrawerOpen]
  );

  const handleCloseCreateGroupChatModal = useCallback(() => {
    setOpenGroupModal(false);
  }, []);

  const handleClickNewGroupChat = useCallback(() => {
    setOpenGroupModal(true);
  }, []);

  const handleToggleMobileDrawer = useCallback(() => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  }, [mobileDrawerOpen]);

  const handleCloseMobileDrawer = useCallback(() => {
    setMobileDrawerOpen(false);
  }, []);

  const handleOpenMobileDrawer = useCallback(() => {
    setMobileDrawerOpen(true);
  }, []);

  const renderExpertsListContent = useCallback(
    (isMobileView: boolean = false) => (
      <>
        <HeaderContainer isMobile={isMobileView}>
          {isDesktop && (
            <Typography
              variant={isMobileView ? "body2" : "body1"}
              fontWeight={600}
              sx={{
                fontSize: { xs: "12px", sm: "13px", md: "14px" },
                color: Colors.ELEGANT_BLACK,
              }}
            >
              {t("lazoExpertsList.title")}
            </Typography>
          )}
          {/* <ButtonGroup> */}
          <ActionButton
            onClick={handleClickNewGroupChat}
            startIcon={<Groups sx={{ fontSize: { xs: "14px", sm: "16px" } }} />}
            isMobile={isMobileView}
            aria-label={t("lazoExpertsList.newGroupChatButton")}
          >
            <Typography
              sx={{
                fontSize: { xs: "10px", sm: "11px", md: "12px" },
                fontWeight: 500,
                display: { xs: isMobileView ? "block" : "none", sm: "block" },
              }}
            >
              {t("lazoExpertsList.newGroupChatButton")}
            </Typography>
          </ActionButton>
          {/* <ActionButton
              onClick={props.onClickSupportChat}
              startIcon={<People sx={{ fontSize: { xs: "14px", sm: "16px" } }} />}
              isMobile={isMobileView}
              aria-label={t("lazoExpertsList.productSupportButton")}
            >
              <Typography
                sx={{
                  fontSize: { xs: "10px", sm: "11px", md: "12px" },
                  fontWeight: 500,
                  display: { xs: isMobileView ? "block" : "none", sm: "block" },
                }}
              >
                {t("lazoExpertsList.productSupportButton")}
              </Typography>
            </ActionButton> */}
          {/* </ButtonGroup> */}
        </HeaderContainer>

        {lazoEmployees.length === 0 ? (
          <EmptyStateContainer>
            <Typography
              sx={{
                fontSize: { xs: "12px", sm: "13px" },
                color: Colors.DARKER_DARKER_GRAY,
                textAlign: "center",
                px: 1,
              }}
              id="experts-list-description"
            >
              {t("lazoExpertsList.noAssignedExpertsMessage")}
            </Typography>
          </EmptyStateContainer>
        ) : (
          <EmployeesList>
            {lazoEmployees.map((employee) => {
              const serviceString = employee.service ? StringUtils.capitalizeWords(EnumsUtils.parseEnumToRender(employee.service)) : "";

              return (
                <LazoTooltip title={isMobileView ? "" : serviceString} placement={"right-end"} key={employee.userExternalId}>
                  <EmployeeItem
                    onClick={() => handleEmployeeClick(employee)}
                    isMobile={isMobileView}
                    role="button"
                    tabIndex={0}
                    aria-label={`Chat with ${employee.fullName}, ${employee.role}`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleEmployeeClick(employee);
                      }
                    }}
                  >
                    <EmployeeAvatar
                      sx={{
                        ...UserAvatarSx,
                        width: { xs: 28, sm: 32, md: 36 },
                        height: { xs: 28, sm: 32, md: 36 },
                        fontSize: { xs: "12px", sm: "14px", md: "16px" },
                      }}
                      user={getUser(employee)}
                    />
                    <EmployeeInfo>
                      <EmployeeHeader>
                        <EmployeeName
                          sx={{
                            fontSize: { xs: "13px", sm: "14px", md: "15px" },
                            fontWeight: 500,
                          }}
                        >
                          {employee.fullName}
                        </EmployeeName>
                        <EmployeeRole
                          sx={{
                            fontSize: { xs: "9px", sm: "10px", md: "11px" },
                          }}
                        >
                          {employee.role.replaceAll("KAM", t("lazoExpertsList.kamServicesSupport"))}
                        </EmployeeRole>
                      </EmployeeHeader>

                      {serviceString && (
                        <EmployeeService
                          sx={{
                            fontSize: { xs: "10px", sm: "11px", md: "12px" },
                          }}
                        >
                          {serviceString}
                        </EmployeeService>
                      )}
                    </EmployeeInfo>
                  </EmployeeItem>
                </LazoTooltip>
              );
            })}
          </EmployeesList>
        )}
      </>
    ),
    [lazoEmployees, t, handleEmployeeClick, handleClickNewGroupChat, props.onClickSupportChat, getUser]
  );

  return (
    <>
      {isDesktop && <DesktopContainer>{renderExpertsListContent(false)}</DesktopContainer>}

      {(isMobile || isTablet) && (
        <>
          <Fade in={!mobileDrawerOpen} timeout={prefersReducedMotion ? 0 : 200} unmountOnExit>
            <MobileFab
              color="primary"
              size="large"
              aria-label="Show experts list"
              onClick={handleToggleMobileDrawer}
              sx={{
                position: "fixed",
                bottom: { xs: "calc(64px + 12px)", sm: "20px" },
                right: { xs: 12, sm: 16 },
                zIndex: theme.zIndex.speedDial,
                backgroundColor: Colors.LAZO_PURPLE,
                color: "white",
                width: { xs: 52, sm: 56 },
                height: { xs: 52, sm: 56 },
                "&:hover": {
                  backgroundColor: Colors.BUTTON_HOVER_PURPLE,
                  transform: "scale(1.05)",
                },
                "&:active": {
                  transform: "scale(0.95)",
                },
                boxShadow: "0 8px 32px rgba(106, 90, 205, 0.3)",
                transition: prefersReducedMotion ? "none" : "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <People fontSize={isMobile ? "small" : "medium"} />
            </MobileFab>
          </Fade>

          <SwipeableDrawer
            anchor="bottom"
            open={mobileDrawerOpen}
            onClose={handleCloseMobileDrawer}
            onOpen={handleOpenMobileDrawer}
            disableSwipeToOpen={false}
            swipeAreaWidth={20}
            hysteresis={0.6}
            minFlingVelocity={400}
            PaperProps={{
              elevation: 24,
              sx: {
                borderTopLeftRadius: { xs: 16, sm: 20 },
                borderTopRightRadius: { xs: 16, sm: 20 },
                maxHeight: { xs: "75vh", sm: "70vh" },
                minHeight: { xs: "50vh", sm: "45vh" },
                background: "linear-gradient(180deg, #ffffff 0%, #fafafa 100%)",
              },
            }}
            ModalProps={{
              keepMounted: true,
              "aria-labelledby": "experts-list-title",
              "aria-describedby": "experts-list-description",
            }}
            transitionDuration={prefersReducedMotion ? 0 : undefined}
          >
            <MobileDrawerContainer>
              <DrawerHandle
                onClick={handleCloseMobileDrawer}
                role="button"
                aria-label="Close experts list"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleCloseMobileDrawer();
                  }
                }}
              />
              <DrawerHeader>
                <HeaderTitle>
                  <People sx={{ color: Colors.LAZO_PURPLE, fontSize: "16px" }} />
                  <Typography
                    id="experts-list-title"
                    sx={{
                      fontSize: { xs: "14px", sm: "16px" },
                      fontWeight: 600,
                      color: Colors.ELEGANT_BLACK,
                    }}
                  >
                    {t("lazoExpertsList.title")}
                  </Typography>
                </HeaderTitle>
                <CloseButton onClick={handleCloseMobileDrawer} aria-label="Close experts list">
                  <Close fontSize="small" />
                </CloseButton>
              </DrawerHeader>
              <DrawerContent>{renderExpertsListContent(true)}</DrawerContent>
            </MobileDrawerContainer>
          </SwipeableDrawer>
        </>
      )}

      <OneGroupChatModal
        open={openGroupModal}
        title={t("groupChatModal.newGroupChatTitle")}
        onClose={handleCloseCreateGroupChatModal}
        onConfirm={props.onCreateGroupChat}
        minSelected={2}
      />
    </>
  );
});

const DesktopContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  backgroundColor: "#fff",
  display: "flex",
  flexDirection: "column",
  height: "100%", // Take full height from parent
  overflow: "visible", // No scroll here, parent handles it
}));

const HeaderContainer = styled(Box)<{ isMobile?: boolean }>(({ theme, isMobile }) => ({
  display: "flex",
  alignItems: "center",
  flexDirection: "row",
  justifyContent: "space-between",
  padding: isMobile ? "8px 12px" : "8px 12px 6px",
  position: "sticky",
  top: 0,
  backgroundColor: "#fff",
  zIndex: 10,
  borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  minHeight: isMobile ? "48px" : "44px",
  gap: theme.spacing(0.75),
  flexShrink: 0,

  [theme.breakpoints.down("sm")]: {
    padding: "6px 8px",
    minHeight: "42px",
  },
}));

const ButtonGroup = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  flexShrink: 0,
  width: "100%",
  justifyContent: "center",

  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(0.25),
  },
}));

const ActionButton = styled(Button)<{ isMobile?: boolean }>(({ theme, isMobile }) => ({
  minHeight: isMobile ? "28px" : "32px",
  padding: isMobile ? "4px 8px" : "6px 10px",
  textTransform: "none",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  borderRadius: "6px",
  backgroundColor: "#fff",
  color: Colors.ELEGANT_BLACK,
  fontWeight: 500,
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",

  "&:hover": {
    backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
    borderColor: Colors.LAZO_PURPLE,
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(106, 90, 205, 0.2)",
  },

  "&:active": {
    transform: "translateY(0)",
  },

  [theme.breakpoints.down("sm")]: {
    minHeight: "26px",
    padding: "3px 6px",
    borderRadius: "4px",
  },
}));

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(2, 1),
  minHeight: "80px",
}));

const EmployeesList = styled(MenuList)(({ theme }) => ({
  padding: theme.spacing(0.25, 0.75),
  flex: 1,
  overflow: "auto",

  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.25, 0.5),
  },
}));

const EmployeeItem = styled(MenuItem)<{ isMobile?: boolean }>(({ theme, isMobile }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  minHeight: isMobile ? "48px" : "56px",
  padding: theme.spacing(0.75, 1),
  marginBottom: theme.spacing(0.25),
  borderRadius: "8px",
  cursor: "pointer",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  border: "1px solid transparent",

  "&:hover": {
    backgroundColor: "rgba(106, 90, 205, 0.06)",
    borderColor: "rgba(106, 90, 205, 0.2)",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 16px rgba(106, 90, 205, 0.1)",
  },

  "&:active": {
    transform: "translateY(0)",
    backgroundColor: "rgba(106, 90, 205, 0.12)",
  },

  "&:focus-visible": {
    outline: `2px solid ${Colors.LAZO_PURPLE}`,
    outlineOffset: "2px",
  },

  [theme.breakpoints.down("sm")]: {
    minHeight: "44px",
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.75),
    borderRadius: "6px",
  },
}));

const EmployeeAvatar = styled(UserAvatar)(({ theme }) => ({
  flexShrink: 0,
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  transition: "all 0.2s ease",

  ".MuiMenuItem-root:hover &": {
    borderColor: Colors.LAZO_PURPLE,
    boxShadow: `0 0 0 1px rgba(106, 90, 205, 0.3)`,
  },
}));

const EmployeeInfo = styled(Box)(() => ({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: "2px",
}));

const EmployeeHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(0.75),

  [theme.breakpoints.down("sm")]: {
    alignItems: "flex-start",
    flexDirection: "column",
    gap: "1px",
  },
}));

const EmployeeName = styled(Typography)(() => ({
  color: Colors.ELEGANT_BLACK,
  fontWeight: 500,
  lineHeight: 1.2,
  flexShrink: 0,
}));

const EmployeeRole = styled(Typography)(({ theme }) => ({
  color: Colors.DARKER_DARKER_GRAY,
  fontWeight: 400,
  lineHeight: 1.2,
  flexShrink: 1,
  textAlign: "right",

  [theme.breakpoints.down("sm")]: {
    textAlign: "left",
  },
}));

const EmployeeService = styled(Typography)(() => ({
  color: Colors.DARKER_DARKER_GRAY,
  fontWeight: 400,
  lineHeight: 1.3,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: "100%",
}));

const MobileFab = styled(Fab)(() => ({}));

const MobileDrawerContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  overflow: "hidden",
  width: "100%",
}));

const DrawerHandle = styled(Box)(({ theme }) => ({
  width: "36px",
  height: "4px",
  backgroundColor: Colors.LIGHT_GRAY_DEDEE1,
  borderRadius: "2px",
  margin: "8px auto 0",
  cursor: "pointer",
  transition: "all 0.2s ease",
  flexShrink: 0,

  "&:hover": {
    backgroundColor: Colors.DARKER_DARKER_GRAY,
    width: "44px",
  },

  "&:active": {
    backgroundColor: Colors.LAZO_PURPLE,
  },

  "&:focus-visible": {
    outline: `2px solid ${Colors.LAZO_PURPLE}`,
    outlineOffset: "2px",
  },
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 16px",
  borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  minHeight: "56px",
  backgroundColor: "#fff",
  position: "sticky",
  top: 0,
  zIndex: 1,
  flexShrink: 0,

  [theme.breakpoints.down("sm")]: {
    padding: "10px 12px",
    minHeight: "48px",
  },
}));

const HeaderTitle = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: Colors.DARKER_DARKER_GRAY,
  backgroundColor: "rgba(0, 0, 0, 0.04)",
  borderRadius: "8px",
  width: "36px",
  height: "36px",
  transition: "all 0.2s ease",

  "&:hover": {
    backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
    color: Colors.LAZO_PURPLE,
    transform: "scale(1.05)",
  },

  "&:active": {
    transform: "scale(0.95)",
  },

  [theme.breakpoints.down("sm")]: {
    width: "32px",
    height: "32px",
    borderRadius: "6px",
  },
}));

const DrawerContent = styled(Box)(() => ({
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  width: "100%",

  "&::-webkit-scrollbar": {
    width: "4px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(106, 90, 205, 0.3)",
    borderRadius: "2px",
  },
}));
